import { useEffect, useState } from 'react';
import { Eye, ShieldCheck } from 'lucide-react';
import {
  getTransactionVisibilityLabel,
  transactionVisibilityValueSchema,
  type TransactionVisibilityValue,
} from '@mls-wallet/shared-types';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useUpdateTransactionVisibility } from '@/hooks/admin/organization/use-update-transaction-visibility';

type TransactionVisibilityModalProps = {
  open: boolean;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
  currentHideTransactionsFromNonPolicyParticipants: boolean;
};

function boolToValue(
  hideTransactionsFromNonPolicyParticipants: boolean
): TransactionVisibilityValue {
  return hideTransactionsFromNonPolicyParticipants
    ? 'policy-participants'
    : 'all-members';
}

export function TransactionVisibilityModal({
  open,
  onClose,
  onSaved,
  currentHideTransactionsFromNonPolicyParticipants,
}: TransactionVisibilityModalProps) {
  const [selectedValue, setSelectedValue] =
    useState<TransactionVisibilityValue>(() =>
      boolToValue(currentHideTransactionsFromNonPolicyParticipants)
    );
  const { updateTransactionVisibility, isLoading, error } =
    useUpdateTransactionVisibility();

  useEffect(() => {
    setSelectedValue(
      boolToValue(currentHideTransactionsFromNonPolicyParticipants)
    );
  }, [currentHideTransactionsFromNonPolicyParticipants]);

  const selectedHideTransactionsFromNonPolicyParticipants =
    selectedValue === 'policy-participants';

  const handleSave = async () => {
    try {
      await updateTransactionVisibility({
        hideTransactionsFromNonPolicyParticipants:
          selectedHideTransactionsFromNonPolicyParticipants,
      });
    } catch {
      return;
    }

    await onSaved();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Update transaction visibility</DialogTitle>
          <DialogDescription className="space-y-3">
            <p>
              Choose whether organization members can view every transaction or
              only transactions where they are part of the policy.
            </p>
            <div className="flex items-start gap-2 rounded-lg border border-border bg-muted/40 p-3 text-sm">
              <Eye className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground" />
              <div className="space-y-1">
                <div>
                  Current setting:{' '}
                  <strong>
                    {getTransactionVisibilityLabel(
                      currentHideTransactionsFromNonPolicyParticipants
                    )}
                  </strong>
                </div>
                <div>
                  New setting:{' '}
                  <strong>
                    {getTransactionVisibilityLabel(
                      selectedHideTransactionsFromNonPolicyParticipants
                    )}
                  </strong>
                </div>
              </div>
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="max-w-sm space-y-2">
          <label
            className="text-sm font-medium"
            htmlFor="transaction-visibility"
          >
            Visibility
          </label>
          <Select
            value={selectedValue}
            onValueChange={(value) =>
              setSelectedValue(transactionVisibilityValueSchema.parse(value))
            }
          >
            <SelectTrigger id="transaction-visibility">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all-members">
                All organization members
              </SelectItem>
              <SelectItem value="policy-participants">
                Only admins, initiators, and approvers
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-start gap-2 rounded-lg border border-border p-3 text-sm">
          <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground" />
          <p className="text-muted-foreground">
            Admins can always see every organization transaction. This setting
            changes visibility for non-admin members only.
          </p>
        </div>

        {error ? (
          <p className="text-sm text-red-600">
            {error.message || 'Failed to update transaction visibility'}
          </p>
        ) : null}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isLoading}>
            {isLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
